import { ScrollView } from "react-native";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { isRuleArmed } from "@src/features/schedules/utils/schedule-utils";
import type { StripeState } from "@src/features/schedules/utils/stripe-pattern";
import { DiagonalStripeSquare } from "./DiagonalStripeSquare";
import { FC, memo } from "react";

interface LegendProps {
  exceptions?: AlarmException[];
  schedule?: AlarmSchedule;
}

export const Legend: FC<LegendProps> = memo(({ exceptions = [], schedule }) => {
  const { theme } = useUnistyles();
  const armedColor = theme.colors.alarmStates.armed as string;
  // Use trackBackground – disarmed segments don't render in the chart, so the visible
  // "disarmed" colour is the track background behind them (disarmed differs from
  // trackBackground in dark mode).
  const disarmedColor = theme.colors.alarmStates.trackBackground as string;
  const textColor = theme.colors.text as string; // Text color for silence

  // Check if there are any armed or disarmed exceptions
  const hasArmedExceptions = exceptions.some((ex) => ex.action === "armed");
  const hasDisarmedExceptions = exceptions.some((ex) => ex.action === "disarmed");

  // Check if schedule has silence or override rules
  const hasSilence =
    schedule?.rules.some((rule) => rule.kind === "absolute_range" && rule.id.startsWith("silence:")) ?? false;
  const overrideRules =
    schedule?.rules.filter((rule) => rule.kind === "absolute_range" && rule.id.startsWith("override:")) ?? [];
  const hasArmedOverride = overrideRules.some((rule) => isRuleArmed(rule.features));
  const hasDisarmedOverride = overrideRules.some((rule) => !isRuleArmed(rule.features));

  // Build array of legend items
  const legendItems: Array<{
    id: string;
    label: string;
    color: string;
    isException?: boolean;
    state?: StripeState;
  }> = [
    { id: "armed", label: "Armed", color: armedColor },
    { id: "disarmed", label: "Disarmed", color: disarmedColor },
  ];

  // Add exception legend items if they exist
  if (hasArmedExceptions) {
    legendItems.push({
      id: "armed-exception",
      label: "Armed by exception",
      color: armedColor,
      isException: true,
      state: "armed",
    });
  }
  if (hasDisarmedExceptions) {
    legendItems.push({
      id: "disarmed-exception",
      label: "Disarmed by exception",
      color: disarmedColor,
      isException: true,
      state: "disarmed",
    });
  }

  // Add silence and override legend items if they exist
  if (hasSilence) {
    legendItems.push({
      id: "silence",
      label: "Silence",
      color: textColor,
    });
  }
  if (hasArmedOverride) {
    legendItems.push({
      id: "armed-override",
      label: "Armed by override",
      color: armedColor,
      isException: true,
      state: "armed",
    });
  }
  if (hasDisarmedOverride) {
    legendItems.push({
      id: "disarmed-override",
      label: "Disarmed by override",
      color: disarmedColor,
      isException: true,
      state: "disarmed",
    });
  }

  return (
    <View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.contentContainerStyle}
      >
        {legendItems.map((item) => (
          <View flexDirection="row" alignItems="center" gap="md" key={item.id}>
            {item.isException && item.state ?
              <DiagonalStripeSquare state={item.state} size={12} borderRadius={2} />
            : <View
                w={12}
                h={12}
                rounded={2}
                style={{
                  backgroundColor: item.color,
                }}
              />
            }
            <Text textStyle="xs/regular" color="text">
              {item.label}
            </Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  contentContainerStyle: {
    paddingLeft: 0,
    paddingRight: theme.spacing["3xl"],
    gap: theme.spacing["3xl"],
    alignItems: "center",
  },
}));
